/**
 * @fileoverview Popover form for creating a new mapping, anchored to the create button.
 * @module features/universal-transactions/components/mapping/CreateMappingPopover
 */

import { useMemo, useState, type ReactElement, type ReactNode } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import type { MappingDefinition } from '../../schemas/universal-transactions'

export interface CreateMappingPopoverProps {
  /** CSV header columns available */
  header: string[]
  /** Called after successful creation to refresh data */
  onCreated?: (() => void) | undefined
  /** Optional custom trigger content (icon/button). */
  children?: ReactNode
  /** Function to save the mapping */
  onSave: (name: string, mapping: MappingDefinition) => Promise<void>
  /** Default name for the mapping */
  defaultName?: string
}

/**
 * Create popover for adding a new mapping
 *
 * @component
 */
export default function CreateMappingPopover({
  header,
  onCreated,
  children,
  onSave,
  defaultName = '',
}: CreateMappingPopoverProps): ReactElement {
  const { t } = useTranslation('features-universal-transactions')
  const [open, setOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const [name, setName] = useState<string>('')
  const [columns, setColumns] = useState<MappingDefinition['columns']>({
    txn_date: undefined,
    amount: undefined,
    currency: undefined,
    title: undefined,
    counterparty_name: undefined,
    direction: undefined,
    status: undefined,
  })

  const required = useMemo(() => ['txn_date', 'amount', 'currency', 'title'] as const, [])

  const setField = (field: keyof MappingDefinition['columns'], value: string): void => {
    setColumns(prev => {
      const existing = prev[field] as Record<string, unknown> | undefined
      if (!value) {
        return { ...prev, [field]: undefined }
      }
      const nextCol = {
        ...(existing ?? {}),
        from: value,
      } as unknown as MappingDefinition['columns'][typeof field]
      return { ...prev, [field]: nextCol }
    })
  }

  const updateAmountParse = (partial: {
    decimal?: string | null
    thousands?: string | null
  }): void => {
    setColumns(prev => {
      const existing = (prev.amount ?? {}) as NonNullable<MappingDefinition['columns']['amount']>
      const currentParse = (existing.parse ?? {}) as Partial<{
        type: 'date' | 'number'
        formats?: string[]
        decimal?: string
        thousands?: string
      }>
      const nextParse: typeof currentParse = { ...currentParse }

      if ('decimal' in partial) {
        if (partial.decimal && partial.decimal.length > 0) nextParse.decimal = partial.decimal
        else delete nextParse.decimal
      }
      if ('thousands' in partial) {
        if (partial.thousands && partial.thousands.length > 0)
          nextParse.thousands = partial.thousands
        else delete nextParse.thousands
      }

      const hasHints =
        typeof nextParse.decimal === 'string' || typeof nextParse.thousands === 'string'
      const nextAmount: NonNullable<MappingDefinition['columns']['amount']> = {
        ...existing,
        parse: hasHints ? ({ type: 'number', ...nextParse } as const) : undefined,
      }
      return { ...prev, amount: nextAmount }
    })
  }

  const handleSave = async (): Promise<void> => {
    const missing = required.filter(f => !columns[f]?.from)
    if (missing.length > 0) {
      toast.error(
        t('mapping.errors.missingRequired', {
          defaultValue: 'Missing required fields: {{fields}}',
          fields: missing.join(', '),
        })
      )
      return
    }

    const mappingDef: MappingDefinition = {
      version: 1,
      columns,
      extras: header.filter(
        col =>
          !Object.values(columns).some(
            m =>
              typeof m === 'object' &&
              m !== null &&
              'from' in (m as Record<string, unknown>) &&
              (m as { from?: unknown }).from === col
          )
      ),
    }

    setIsSaving(true)
    try {
      await onSave(name || 'Unnamed mapping', mappingDef)
      setOpen(false)
      // Reset form
      setName(defaultName)
      setColumns({
        txn_date: undefined,
        amount: undefined,
        currency: undefined,
        title: undefined,
        counterparty_name: undefined,
        direction: undefined,
        status: undefined,
      })
      onCreated?.()
    } catch (e: unknown) {
      console.error('[CreateMappingPopover] Save failed', e)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = (): void => {
    setOpen(false)
    // Reset form
    setName(defaultName)
    setColumns({
      txn_date: undefined,
      amount: undefined,
      currency: undefined,
      title: undefined,
      counterparty_name: undefined,
      direction: undefined,
      status: undefined,
    })
  }

  const FieldRow = ({
    fieldKey,
    label,
    isRequired = false,
  }: {
    fieldKey: keyof MappingDefinition['columns']
    label: string
    isRequired?: boolean
  }): ReactElement => {
    const fromVal = (columns[fieldKey] as { from?: string | string[] } | undefined)?.from
    const selectValue = Array.isArray(fromVal) ? (fromVal[0] ?? '') : (fromVal ?? '')
    return (
      <div className="grid grid-cols-3 gap-x-4 items-center">
        <Label className="text-sm text-muted-foreground col-span-1">
          {label}
          {isRequired && <span className="text-destructive ml-1">*</span>}
        </Label>
        <select
          className="col-span-2 rounded-md border border-border bg-background text-foreground px-3 py-2 text-sm"
          value={selectValue}
          onChange={e => setField(fieldKey, e.target.value)}
        >
          <option value="">-- Select column --</option>
          {header.map(col => (
            <option key={col} value={col}>
              {col}
            </option>
          ))}
        </select>
      </div>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children ?? (
          <Button
            type="button"
            size="sm"
            aria-label={t('mapping.popover.createAriaLabel', { defaultValue: 'Create new mapping' })}
          >
            {t('mapping.button.createNew', { defaultValue: 'Create new mapping' })}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-[560px] p-5 rounded-lg shadow-lg" align="end" sideOffset={8}>
        <div className="text-xl font-semibold mb-1">
          {t('mapping.popover.createTitle', { defaultValue: 'Our computer is dumb, help it:' })}
        </div>
        <div className="text-sm text-muted-foreground mb-4">
          {t('mapping.popover.createSubtitle', { defaultValue: 'For each field below, choose corresponding column from your file' })}
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-x-4 items-center">
            <Label className="text-sm text-muted-foreground col-span-1">
              {t('mapping.nameLabel', { defaultValue: 'Template name' })}
            </Label>
            <Input
              className="col-span-2"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={t('mapping.namePlaceholder', { defaultValue: 'e.g., ING Bank, mBank, Pekao csv' })}
            />
          </div>

          <div className="border-t border-border my-2" />

          <FieldRow
            fieldKey="txn_date"
            label={t('mapping.fields.txnDate', { defaultValue: 'Transaction date?' })}
            isRequired
          />

          <FieldRow
            fieldKey="amount"
            label={t('mapping.fields.amount', { defaultValue: 'How much?' })}
            isRequired
          />

          {/* Amount parsing hints */}
          <div className="grid grid-cols-3 gap-x-4">
            <Label className="text-sm text-muted-foreground col-span-1">
              {t('mapping.parsing.title', { defaultValue: 'How to read numbers' })}
            </Label>
            <div className="col-span-2 flex gap-3">
              <div className="flex flex-col gap-1 flex-1">
                <Label className="text-xs" htmlFor="amount-decimal">
                  {t('mapping.parsing.decimalLabel', { defaultValue: 'Decimal separator' })}
                </Label>
                <select
                  id="amount-decimal"
                  className="border rounded-md h-9 px-2 text-sm"
                  value={(columns.amount?.parse?.decimal as string | undefined) ?? ''}
                  onChange={e => updateAmountParse({ decimal: e.target.value || null })}
                >
                  <option value="">
                    {t('mapping.parsing.decimal.auto', { defaultValue: 'Auto' })}
                  </option>
                  <option value=",">
                    {t('mapping.parsing.decimal.comma', { defaultValue: 'Comma (,)' })}
                  </option>
                  <option value=".">
                    {t('mapping.parsing.decimal.dot', { defaultValue: 'Period (.)' })}
                  </option>
                </select>
              </div>
              <div className="flex flex-col gap-1 flex-1">
                <Label className="text-xs" htmlFor="amount-thousands">
                  {t('mapping.parsing.thousandsLabel', { defaultValue: 'Thousands separator' })}
                </Label>
                <select
                  id="amount-thousands"
                  className="border rounded-md h-9 px-2 text-sm"
                  value={(columns.amount?.parse?.thousands as string | undefined) ?? ''}
                  onChange={e => updateAmountParse({ thousands: e.target.value || null })}
                >
                  <option value="">
                    {t('mapping.parsing.thousands.none', { defaultValue: 'None' })}
                  </option>
                  <option value=" ">
                    {t('mapping.parsing.thousands.space', { defaultValue: 'Space' })}
                  </option>
                  <option value="\u00A0">
                    {t('mapping.parsing.thousands.nbsp', { defaultValue: 'NBSP' })}
                  </option>
                  <option value=",">
                    {t('mapping.parsing.thousands.comma', { defaultValue: 'Comma (,)' })}
                  </option>
                  <option value=".">
                    {t('mapping.parsing.thousands.dot', { defaultValue: 'Period (.)' })}
                  </option>
                  <option value="'">
                    {t('mapping.parsing.thousands.apostrophe', {
                      defaultValue: "Apostrophe (')",
                    })}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <FieldRow
            fieldKey="currency"
            label={t('mapping.fields.currency', { defaultValue: 'What currency?' })}
            isRequired
          />

          <FieldRow
            fieldKey="title"
            label={t('mapping.fields.title', {
              defaultValue: 'Transaction title?',
            })}
            isRequired
          />

          <FieldRow
            fieldKey="counterparty_name"
            label={t('mapping.fields.counterparty', { defaultValue: 'Who was counterparty?' })}
          />

          <div className="flex items-center justify-end gap-2 mt-2 pt-2 border-t">
            <Button type="button" variant="outline" onClick={handleCancel}>
              {t('mapping.actions.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              type="button"
              variant="default"
              disabled={isSaving}
              onClick={() => void handleSave()}
            >
              {isSaving
                ? t('mapping.popover.saving', { defaultValue: 'Saving...' })
                : t('mapping.actions.save', { defaultValue: 'Save template' })}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
